import {
  DateInput,
  TextInput,
  InfoBlock,
  Dropdown,
  MultiInput,
  PhoneInput,
  EmailInput,
  useTranslator,
  getLocale,
  FileUploader,
} from "@sia/style-guide";
import { useCallback, useContext, useEffect, useState } from "react";
import { WizarContext } from "contexts/WizardContext";
import { studentPersonalInfoActionCreator } from "contexts/WizardContext";
import webPlatformAdapter from "common/Adapters/WebPlatformAdapter/WebPlatformAdapter";
import pgAdapter from "common/Adapters/PGAdapter/PGAdapter";
import GlobalContext from "contexts/GlobalContext";
import { initialWizardFormState } from "contexts/WizardContext";

const PersonalInfo = ({ errors, setErrors }) => {
  const translate = useTranslator();
  const { language } = useContext(GlobalContext);
  const { state, dispatch } = useContext(WizarContext);
  let personalInfo = state.student.personalInfo;
  let personalInfoErrors = errors.student.personalInfo;
  let changState = useCallback((payload) => dispatch(studentPersonalInfoActionCreator(payload)), [dispatch]);

  const resetInputFieldError = (property, error) => {
    const exculdedProperties = ["phone", "mobile", "email"];
    if (!exculdedProperties.includes(property)) {
      setErrors((errorsState) => ({
        ...errorsState,
        student: {
          ...errorsState.student,
          personalInfo: { ...errorsState.student.personalInfo, [property]: error },
        },
      }));
    } else {
      exculdedProperties.forEach((exculdedProperty) => {
        if (property === exculdedProperty)
          setErrors((errorsState) => ({
            ...errorsState,
            student: {
              ...errorsState.student,
              personalInfo: { ...errorsState.student.personalInfo, [exculdedProperty]: error },
            },
          }));
      });
    }
  };

  const { user } = useContext(GlobalContext);
  const [countries, setCountries] = useState([]);
  const [cantons, setCantons] = useState([]);
  const [studyTypes, setStudyTypes] = useState([]);
  const [schools, setSchools] = useState([]);

  const userId = user.siaId;

  useEffect(() => {
    async function getLists() {
      let countries = await webPlatformAdapter.request("GET", "rpc/get_countries_list", { lang: getLocale(language) }, null);
      countries = countries.map((e) => ({ ...e, label: e.name }));
      setCountries(countries);

      let states = await webPlatformAdapter.request("GET", "rpc/get_states_list", { lang: getLocale(language) }, null);
      states = states.map((e) => ({ ...e, label: e.name }));
      setCantons(states);
    }
    getLists();
  }, [language]);

  useEffect(() => {
    async function getLists() {
      let studyTypes = await webPlatformAdapter.request("GET", "rpc/get_study_types_list", null, null);
      studyTypes = studyTypes.map((e) => ({ id: e.id, label: e.name }));
      setStudyTypes(studyTypes);
      let schools = await webPlatformAdapter.request("GET", "rpc/get_schools_list", null, null);
      schools = schools.map((e) => ({ id: e.id, label: e.name }));
      setSchools(schools);
    }
    if (user) {
      getLists();
    }
  }, [user, language]);

  useEffect(() => {
    async function getCustomerDataOfMemberTypeSingle() {
      let data = await webPlatformAdapter.getCustomerDataOfMemberTypeSingle(language);
      data = data[0];
      let gender = data.gender;
      let firstName = data.firstname;
      let lastName = data.lastname;
      let nationality = data.language;
      let diplomTitle = data.diploma_title;
      let email = data.login_email;
      changState({ gender, firstName, lastName, nationality, diplomTitle, email });
    }
    getCustomerDataOfMemberTypeSingle();
  }, [language, changState]);

  useEffect(() => {
    if (userId) {
      pgAdapter.getWizardSession(({ data }) => {
        const defaultPersonInfo = { ...initialWizardFormState.student.personalInfo };
        const { personalInfo } = data?.student || {};
        const {
          dateOfBirth,
          language,
          academicTitle,
          additionalInfo,
          street,
          zip,
          city,
          country,
          canton,
          schoolName,
          studyType,
          studyStart,
          studyEnd,
          proofOfEnrollmentFile,
        } = personalInfo || defaultPersonInfo;
        changState({
          dateOfBirth,
          language,
          academicTitle,
          additionalInfo,
          street,
          zip,
          city,
          country,
          canton,
          schoolName,
          studyType,
          studyStart,
          studyEnd,
          proofOfEnrollmentFile,
        });
      });
    }
  }, [userId, changState]);

  const setUserSession = (obj) => {
    pgAdapter.getWizardSession(({ data }) => {
      const updatedSession = { ...data, student: { ...(data.student || {}), personalInfo: { ...(data?.student?.personalInfo || {}), ...obj } } };
      pgAdapter.setWizardSession({ ...updatedSession });
    });
  };

  const languages = [
    { label: "DE", id: "de" },
    { label: "FR", id: "fr" },
    { label: "IT", id: "it" },
    { label: "EN", id: "en" },
  ];

  return (
    <div className="personal-info-page">
      <InfoBlock>
        <span className="label">{translate("wizard.form.single.personal-info.gender.label")}</span>
        <p>{personalInfo.gender}</p>
        <span className="label">{translate("wizard.form.single.personal-info.first-name.label")}</span>
        <p>{personalInfo.firstName}</p>
        <span className="label">{translate("wizard.form.single.personal-info.last-name.label")}</span>
        <p>{personalInfo.lastName}</p>
        <span className="label">{translate("wizard.form.single.personal-info.nationality.label")}</span>
        <p>{personalInfo.nationality}</p>
      </InfoBlock>
      <div className="sia-margin-03-top"></div>
      <DateInput
        onBlur={() => {
          if (personalInfo.dateOfBirth) {
            setUserSession({ dateOfBirth: personalInfo.dateOfBirth });
          }
        }}
        id={"personal-page-birth-date"}
        required={true}
        label={translate("wizard.form.single.personal-info.birth-date.label")}
        placeholder={"01.01.2000"}
        value={personalInfo.dateOfBirth}
        onChange={(event, value, error) => {
          resetInputFieldError("dateOfBirth", error);
          changState({ dateOfBirth: value });
        }}
        invalidPatternText={translate("form.input.error.invalid.with-example", { example: "d.m.yyyy" })}
        invalidText={personalInfoErrors.dateOfBirth}
      />
      <Dropdown
        id={"personal-page-language"}
        label={translate("wizard.form.single.personal-info.language.label")}
        items={languages}
        value={personalInfo.language}
        required={true}
        onChange={(event, value) => {
          resetInputFieldError("language");
          changState({ language: value });
          setUserSession({ language: value });
        }}
        placeholder={translate("wizard.form.single.placeholder")}
        invalid={personalInfoErrors.language}
        invalidText={personalInfoErrors.language}
      />
      <p className="productive-heading-01 sia-padding-03-top sia-padding-03-bottom">{translate("wizard.form.single.personal-info.address.title")} </p>
      <TextInput
        onBlur={() => {
          if (personalInfo.additionalInfo) {
            setUserSession({ additionalInfo: personalInfo.additionalInfo });
          }
        }}
        id={"personal-page-additional-info"}
        value={personalInfo.additionalInfo}
        onChange={(event, value) => {
          resetInputFieldError("additionalInfo");
          changState({ additionalInfo: value });
        }}
        label={translate("wizard.form.single.personal-info.additional-info.label")}
        invalidPatternText={personalInfoErrors.additionalInfo}
        invalidText={personalInfoErrors.additionalInfo}
      />
      <TextInput
        onBlur={() => {
          if (personalInfo.street) {
            setUserSession({ street: personalInfo.street });
          }
        }}
        id={"personal-page-street"}
        value={personalInfo.street}
        placeholder={translate("wizard.form.single.personal-info.steet.placeholder")}
        onChange={(event, value) => {
          resetInputFieldError("street");
          changState({ street: value });
        }}
        label={translate("wizard.form.single.personal-info.street.label")}
        required={true}
        invalidPatternText={personalInfoErrors.street}
        invalidText={personalInfoErrors.street}
      />
      <TextInput
        onBlur={() => {
          if (personalInfo.zip) {
            setUserSession({ zip: personalInfo.zip });
          }
        }}
        id={"personal-page-zip"}
        value={personalInfo.zip}
        placeholder={translate("wizard.form.single.personal-info.zip.placeholder")}
        onChange={(event, value) => {
          resetInputFieldError("zip");
          changState({ zip: value });
        }}
        label={translate("wizard.form.single.personal-info.zip.label")}
        required={true}
        invalidPatternText={personalInfoErrors.zip}
        invalidText={personalInfoErrors.zip}
      />
      <TextInput
        onBlur={() => {
          if (personalInfo.city) {
            setUserSession({ city: personalInfo.city });
          }
        }}
        id={"personal-page-city"}
        value={personalInfo.city}
        placeholder={translate("wizard.form.single.personal-info.city.placeholder")}
        onChange={(event, value) => {
          resetInputFieldError("city");
          changState({ city: value });
        }}
        label={translate("wizard.form.single.personal-info.city.label")}
        required={true}
        invalidPatternText={personalInfoErrors.city}
        invalidText={personalInfoErrors.city}
      />
      <Dropdown
        id={"personal-page-country"}
        label={translate("wizard.form.single.personal-info.country.label")}
        items={countries}
        value={personalInfo.country}
        required={true}
        onChange={(event, value) => {
          resetInputFieldError("country");
          changState({ country: value });
          setUserSession({ country: value });
        }}
        placeholder={translate("wizard.form.single.placeholder")}
        invalidPatternText={personalInfoErrors.country}
        invalidText={personalInfoErrors.country}
      />
      <Dropdown
        id={"personal-page-canton"}
        label={translate("wizard.form.single.personal-info.canton.label")}
        items={cantons}
        value={personalInfo.canton}
        required={true}
        onChange={(event, value) => {
          resetInputFieldError("canton");
          changState({ canton: value });
          setUserSession({ canton: value });
        }}
        placeholder={translate("wizard.form.single.placeholder")}
        invalidPatternText={personalInfoErrors.canton}
        invalidText={personalInfoErrors.canton}
      />
      <EmailInput
        id={"personal-page-email"}
        label={translate("wizard.form.single.personal-info.email.label")}
        value={personalInfo.email}
        onChange={(event, value, error) => {
          resetInputFieldError("email", error);
          changState({ email: value });
        }}
        invalidPatternText={translate("form.input.error.invalid.with-example", { example: translate("form.input.error.invalid.example.email") })}
        invalidText={personalInfoErrors.email}
      />
      <MultiInput id={"contact"} invalidText={null} translate={translate}>
        <PhoneInput
          id={"personal-page-phone"}
          label={translate("wizard.form.single.personal-info.telephone.label")}
          value={personalInfo.phone}
          onChange={(event, value, error) => {
            resetInputFieldError("phone", error);
            changState({ phone: value });
          }}
          invalidPatternText={translate("form.input.error.invalid.with-example", { example: translate("form.input.error.invalid.example.phone") })}
          invalidText={personalInfoErrors.phone}
        />

        <PhoneInput
          id={"personal-page-mobile"}
          label={translate("wizard.form.single.personal-info.mobile.label")}
          value={personalInfo.mobile}
          onChange={(event, value, error) => {
            resetInputFieldError("mobile", error);
            changState({ mobile: value });
          }}
          invalidPatternText={translate("form.input.error.invalid.with-example", { example: translate("form.input.error.invalid.example.phone") })}
          invalidText={personalInfoErrors.mobile}
        />
      </MultiInput>
      <p className="productive-heading-01 sia-padding-03-top sia-padding-03-bottom">{translate("wizard.form.student.study-section.title")} </p>
      <Dropdown
        id={"education-page-school"}
        label={translate("wizard.form.single.education.bachelor.school-name.label")}
        items={schools}
        value={personalInfo.schoolName}
        required={true}
        onChange={(event, value) => {
          resetInputFieldError("schoolName");
          changState({ schoolName: value });
          setUserSession({ schoolName: value });
        }}
        placeholder={translate("wizard.form.single.placeholder")}
        invalid={personalInfoErrors.schoolName}
        invalidText={personalInfoErrors.schoolName}
      />
      <Dropdown
        id={"education-page-study-type"}
        label={translate("wizard.form.single.education.bachelor.study-type.label")}
        items={studyTypes}
        value={personalInfo.studyType}
        required={true}
        onChange={(event, value) => {
          resetInputFieldError("studyType");
          changState({ studyType: value });
          setUserSession({ studyType: value });
        }}
        placeholder={translate("wizard.form.single.placeholder")}
        invalid={personalInfoErrors.studyType}
        invalidText={personalInfoErrors.studyType}
      />
      <DateInput
        onBlur={() => {
          if (personalInfo.studyStart) {
            setUserSession({ studyStart: personalInfo.studyStart });
          }
        }}
        id={"personal-page-studyStart"}
        required={true}
        label={translate("wizard.form.student.study-section.study-start")}
        placeholder={"01.01.2000"}
        value={personalInfo.studyStart}
        onChange={(event, value, error) => {
          resetInputFieldError("studyStart", error);
          changState({ studyStart: value });
        }}
        invalidPatternText={translate("form.input.error.invalid.with-example", { example: "d.m.yyyy" })}
        invalidText={personalInfoErrors.studyStart}
      />
      <DateInput
        onBlur={() => {
          if (personalInfo.studyEnd) {
            setUserSession({ studyEnd: personalInfo.studyEnd });
          }
        }}
        id={"personal-page-studyEnd"}
        required={true}
        label={translate("wizard.form.student.study-section.study-end")}
        placeholder={"01.01.2000"}
        value={personalInfo.studyEnd}
        onChange={(event, value, error) => {
          resetInputFieldError("studyEnd", error);
          changState({ studyEnd: value });
        }}
        invalidPatternText={translate("form.input.error.invalid.with-example", { example: "d.m.yyyy" })}
        invalidText={personalInfoErrors.studyEnd}
      />
      <FileUploader
        label={translate("wizard.form.student.study-section.proof-file")}
        sizeLimit={500}
        sizeLimitUnit={"KB"}
        files={personalInfo.proofOfEnrollmentFile}
        fileTypes={["pdf"]}
        buttonLabel={"wizard.form.single.education.bachelor.upload.button.label"}
        translate={translate}
        onChange={(event, files, error) => {
          resetInputFieldError("proofOfEnrollmentFile");
          changState({ proofOfEnrollmentFile: files });
        }}
        onRemove={(event, file, updatedFiles, error) => {
          resetInputFieldError("proofOfEnrollmentFile");
          changState({ proofOfEnrollmentFile: updatedFiles });
        }}
        invalidText={personalInfoErrors.proofOfEnrollmentFile}
      />
    </div>
  );
};

export default PersonalInfo;
